import authBookingInit   from './steps/authBookingInit.js';
import authLoginAlt      from './steps/authLoginAlt.js';
import authLogin         from './steps/authLogin.js';
import authRegister      from './steps/authRegister.js';
import authPhone         from './steps/authPhone.js';
import authSms           from './steps/authSms.js';
import authResetPassword from './steps/authResetPassword.js';
import authCheckEmail    from './steps/authCheckEmail.js';
import {t} from "../i18n";
import {hasPhone} from './authState.js';
import {showValidationErrors} from '../utils/formHelpers';
import {logClientEvent, describeError} from '../utils/jsErrorLogger.js';
import {isTrustedPopupOrigin, embedOrigin, withQueryParam, classifyPopupMessage, claimedUser} from './oauthPopup.js';

const stepModules = {
    'booking-init': authBookingInit,
    'booking-login-alt': authLoginAlt,
    'booking-login': authLogin,
    'booking-register': authRegister,
    'booking-phone': authPhone,
    'booking-sms': authSms,
    'booking-reset-password': authResetPassword,
    'booking-check-email': authCheckEmail,
    'booking-done': authBookingInit, // TODO: add a screen for back
};

export default function authController({ $, api, state, authData, routes, onComplete, onSocialRedirect }) {

    const auth = authData || {
        isAuth: false,
        isChecked: false,
        skipConfirmation: false,
        skipValidate: false,
        skipUser: false,
        user: null,
    };
    const stepInstances = {};
    const sharedState = {};
    const shared = {
        $, api, state, data: auth, routes, emit, goToStep, complete, sharedState
    };
    let currentStepId = auth.isAuth ? (auth.isChecked === false ? 'booking-phone' : 'booking-done') : 'booking-init';
    if(auth.isAuth) {
        // Null-safe: pe drumul popup-ului auth.user poate lipsi deși isAuth e true. Telefonul/emailul
        // deja din auth.state (sămânța de la încărcare sau re-auth-ul de la Finalize) rămân; restul
        // (noAccount, codeViaGuest) NU se moștenesc.
        const prev = (auth.state && typeof auth.state === 'object') ? auth.state : {};
        const user = (auth.user && typeof auth.user === 'object' && !Array.isArray(auth.user)) ? auth.user : {};
        sharedState.email = prev.email || user.email;
        sharedState.phone = prev.phone || user.phone;
    } else if (auth.skipUser === true) {
        sharedState.noAccount = 1;
        currentStepId = "booking-phone";
    }
    const container = $('<div class="auth-controller"></div>');
    container.on('click', '[data-step]', (e) => {
        e.preventDefault();
        const backStep = $(e.currentTarget).data('step');
        if (backStep) {
            goToStep(backStep);
        }
    });
    auth.state = sharedState;

    function navigateToTop(url) {
        if (url &&
            typeof window !== 'undefined' &&
            typeof window.top !== 'undefined' &&
            typeof window.location !== 'undefined' &&
            typeof window.top.location !== 'undefined'
        ) {
            // Ensure we're not in an iframe
            if (window.top !== window.self) {
                window.top.location.href = url;
            } else {
                window.location.href = url;
            }
        }
    }

    // Ascultătorul `message` al popup-ului social (null = niciun flux în curs).
    let popupListener = null;

    function stopPopupListener() {
        if (popupListener) {
            window.removeEventListener('message', popupListener);
            popupListener = null;
        }
    }

    /**
     * Mesajul popup-ului social (embeddedConfirm). Deciziile stau în oauthPopup.js; aici doar legăturile.
     * Server nou: oauth-success vine cu `claim`, iar login-ul există abia după POST routes.oauthClaim (claim
     * unic, legat de tokenul de sesiune al ACESTUI widget). Server vechi (fără claim): exact ca înainte.
     */
    function onPopupMessage(event) {
        if (!isTrustedPopupOrigin(event.origin)) return;
        const msg = classifyPopupMessage(event.data);
        if (msg.kind === 'ignore') return;

        stopPopupListener(); // primul mesaj tratat închide fluxul
        try {
            // Popup-ul se închide la confirmare (altfel singur, după 2 s).
            if (event.source && typeof event.source.postMessage === 'function') {
                event.source.postMessage('received', event.origin);
            }
        } catch (e) { /* popup deja închis */ }

        if (msg.kind === 'legacy') {
            acceptSocialUser(msg.user);
            return;
        }
        if (msg.kind === 'error') {
            showSocialError(null, 'oauth-error');
            return;
        }
        if (!routes.oauthClaim) {
            // Server cu claim, dar fără ruta în apiRoutes: identitatea nerevendicată NU e login.
            showSocialError(null, 'oauthClaim route missing');
            return;
        }
        api.post(routes.oauthClaim, {
            data: {claim: msg.claim},
            success: (res) => {
                const user = claimedUser(res);
                if (!user) {
                    showSocialError(null, 'oauthClaim 200 without user');
                    return;
                }
                acceptSocialUser(user);
            },
            // 403 claim_token_mismatch / 410 claim_invalid / rețea: codul-mașină nu se afișează.
            error: (err) => showSocialError(err, 'oauthClaim failed')
        });
    }

    /** Continuarea de după un login social reușit (neschimbată față de handlerul vechi). */
    function acceptSocialUser(u) {
        auth.isAuth = true;
        // Ținem minte cine e (nume/email/telefon): pe drumul de oaspete al
        // pasului de telefon serverul nu mai are sesiunea, iar complete()
        // completează de aici ce lipsește din răspunsul gol al validării.
        auth.user = u || auth.user;
        if(u && u.phone_check === 1 && hasPhone(u.phone)) {
            // complete(), nu onComplete(): altfel telefonul/numele nu intrau în
            // sharedState, iar Finalize pleca iar cu phone:null.
            complete({user: u});
        } else {
            // phone requires validation
            sharedState.email = (u && u.email) || '';
            sharedState.phone = (u && u.phone) || '';
            goToStep('booking-phone');
        }
    }

    /** Linia de eroare generică a pașilor de auth + un rând de telemetrie (fără claim / date personale). */
    function showSocialError(err, reason) {
        const status = err?.status ?? null;
        showValidationErrors(container, {_generic: t(status === 419 ? 'booking_session_expired' : 'auth_error_generic')});
        logClientEvent({
            message: 'social login failed',
            status,
            step: 'oauth',
            context: err ? `${reason} | ${describeError(err)}` : reason,
        });
    }

    function emit(event, payload) {
        switch (event) {
            case 'social_login_clicked':
                saveState();
                try {
                    localStorage.setItem('bookingWidgetUrl', window.location.href);
                } catch (e) { /* storage blocked — social return-URL just won't persist */ }
                let url;
                let urlFailedPopup;
                if (payload === 'google') {
                    url = routes.redirectToGoogle;
                    urlFailedPopup = routes.redirectToGooglePopup;
                } else if (payload === 'facebook') {
                    url = routes.facebookRedirect;
                    urlFailedPopup = routes.facebookRedirectPopup;
                } else {
                    console.warn(`Unknown social route: ${payload}`);
                    return;
                }

                if(authData.isEmbedded) {
                    const windowTitle = t('login_with_platform', {platform: t('platform_' + payload)});
                    // DEBUG toggle — Chrome always allows a popup opened straight from a click, so the
                    // popup blocker can't trigger the blocked branch. Force it for testing with:
                    //   localStorage.setItem('bwForcePopupBlock','1')   (or add ?bwForcePopupBlock=1 to the URL)
                    // Turn off with localStorage.removeItem('bwForcePopupBlock'). Inert unless explicitly set.
                    let bwForcePopupBlockStored = false;
                    try {
                        // typeof does NOT protect here — accessing localStorage at all
                        // throws SecurityError when the browser blocks site data.
                        bwForcePopupBlockStored = localStorage.getItem('bwForcePopupBlock') === '1';
                    } catch (e) { /* storage blocked */ }
                    const bwForcePopupBlock =
                        bwForcePopupBlockStored ||
                        /[?&]bwForcePopupBlock=1(?:&|$)/.test(window.location.search || '');
                    // bw_origin: serverul trimite rezultatul popup-ului DOAR către originea asta (nu '*').
                    const popupUrl = withQueryParam(url, 'bw_origin', embedOrigin(window.location));
                    const popup = bwForcePopupBlock ? null : window.open(popupUrl, windowTitle, 'width=600,height=600');

                    if (!popup || popup.closed || typeof popup.closed == 'undefined') {
                        // Popup was blocked: persist the CURRENT widget state server-side.
                        // Use the `state` param (= bwState, the same object the client save persists) —
                        // `window.state` was never assigned anywhere, so this used to POST `undefined`.
                        const stateJson = JSON.stringify(state);

                        // Send the state to the server before redirecting
                        // Go through api.post so the account token (Authorization: Bearer)
                        // rides along — that bypasses CSRF server-side (TokenParser), so no
                        // bogus '{{ csrf_token() }}' and no cross-site session cookie needed.
                        api.post(routes.storeWindowState, {
                            data: { state: stateJson },
                            success: (result) => {
                                // Server cached the state under a random id; carry it through
                                // the top-level social-login redirect so the first-party
                                // redirect route can rehydrate it into the session.
                                const id = result && result.stateId;
                                const dest = id
                                    ? urlFailedPopup + (urlFailedPopup.includes('?') ? '&' : '?') + 'bwState=' + encodeURIComponent(id)
                                    : urlFailedPopup;
                                navigateToTop(dest);
                            },
                            error: (err) => {
                                console.error('Error storing state on the server:', err);
                                navigateToTop(urlFailedPopup); // proceed to login anyway
                            }
                        });
                        return;
                    }

                    // Listen for postMessage from the popup. Un singur ascultător: înainte se adăuga
                    // unul nou la fiecare click social, iar un login ulterior rula handlerul de N ori.
                    stopPopupListener();
                    popupListener = onPopupMessage;
                    window.addEventListener('message', popupListener);
                } else {

                    navigateToTop(url);
                }
                break;

            case 'auth_error':
                console.error('Auth error:', payload);
                break;

            case 'go_back':
                goToStep(payload.step);
                break;

            default:
                console.warn(`Unknown event: ${event}`);
        }
    }

    function goToStep(stepId, payload = {}) {
        if (!stepModules[stepId]) {
            console.warn(`Unknown auth step: ${stepId}`);
            return;
        }
        currentStepId = stepId;
        renderStep(stepId, payload);
    }

    function complete(result) {
        // Null-safe: un apel fără argument (sau un corp JSON `null`) destructura undefined și arunca.
        const {user} = (result && typeof result === 'object') ? result : {};
        // TODO: data may contain future tokens (jwt,stateless,TBD)
        console.log("User logged in:", user);
        console.log("\tName:", user?.name);
        // Re-auth de la Finalize al unui OASPETE (sesiunea a murit): controllerul nou nu moștenește
        // noAccount în sharedState (pasul de telefon ar lua scurtătura skipValidate fără SMS), deci
        // flag-ul vechi se păstrează aici — altfel thanks.js arăta „programările mele” cuiva fără cont.
        // Doar când validarea n-a adus un utilizator: un login real în ecranul de re-auth îl anulează.
        const reauthNoUser = !!auth.reauth
            && !(user && typeof user === 'object' && !Array.isArray(user) && Object.keys(user).length);
        state.noAccount = sharedState.noAccount ?? (reauthNoUser ? state.noAccount : undefined) ?? 0;
        // Propagate the contact into sharedState: confirm.js sends
        // bwAuth.state.phone with the booking. Until now this was set only at
        // page load (already-logged) or in the phone steps — a fresh in-widget
        // LOGIN left it empty, so the POST carried phone=null. For a browser
        // that refuses session cookies that meant: server sees "not logged in"
        // AND has no phone → nothing to book with → the "session expired" loop
        // (account 8, 2026-09-01; the szilard NULL-phone 500 was the same hole).
        // With the phone present, the server books it as guest and the client
        // gets their appointment even cookie-less.
        //
        // Pe drumul de oaspete al pasului de telefon (validatePhone 404 → generateCode), validateCode
        // răspunde 202 cu corp gol, deci `user` e [] / undefined, deși pagina ȘTIE cine e (auth.user
        // din login sau din popup-ul Google). Fără completare, rezervarea sessionless pleca fără nume
        // și ajungea în agendă ca „Fara cont” (review 9 sept 2026). Ordinea: ce confirmă serverul
        // acum > ce a tastat omul în widget (telefonul validat) > ce știa pagina.
        const fresh = (user && typeof user === 'object' && !Array.isArray(user)) ? user : {};
        const known = (auth.user && typeof auth.user === 'object' && !Array.isArray(auth.user)) ? auth.user : {};
        sharedState.phone = fresh.phone || sharedState.phone || known.phone || sharedState.phone;
        sharedState.email = fresh.email || sharedState.email || known.email || sharedState.email;
        if (!state.bookingName && (fresh.name || known.name)) state.bookingName = fresh.name || known.name;
        const merged = { ...known, ...fresh };
        onComplete?.(Object.keys(merged).length ? merged : user);
    }

    function saveState() {
        onSocialRedirect();
    }

    function renderStep(stepId, payload = {}) {
        const Step = stepModules[stepId];
        const instance = Step({
            ...shared,
            onNext: (nextStep, stepData = {}) => goToStep(nextStep, stepData),
            onComplete: complete,
            onSaveState: saveState,
        });

        stepInstances[stepId] = instance;
        container.html('');
        container.append(instance.render(payload));
    }

    return {
        render(parent) {
            parent.append(container);
            renderStep(currentStepId);
        },

        // Optional API
        goToStep,
        getCurrentStep: () => currentStepId,
        restart: () => goToStep('booking-init'),
        getSharedState: () => shared.sharedState
    };
}
